import React, { useState, useRef, useEffect, useCallback } from "react";
import { type NavigateOptions } from "react-router-dom";
import { jsPDF } from 'jspdf';
import { create as createQr } from "qrcode";
import LetterEditorSkeletonLoader from "../../skeletons/LetterEditorSkeletonLoader";
import html2canvas from 'html2canvas';
import "./SchoolLetterHeader.css";
import apiClient from "../../stateManagers/services/axiosInterceptor";
import "primeicons/primeicons.css";
import LetterEditor from "./LetterEditor";
import type { CrudProps } from "../../router/routeWrappers";
import UnsavedChangesModal from "../../components/UnsavedChangesModal";
import { useOptionFiles } from "../../stateManagers/hooks/useOptionFiles";
import { useOptions } from "../../stateManagers/hooks/useOptions";

export const PAGE_W = 794;
export const PAGE_H = 1123;
export const SNAP = 4;
export const ZOOM_STEPS = [0.25, 0.33, 0.5, 0.67, 0.75, 0.9, 1.0, 1.1, 1.25, 1.5, 1.75, 2.0];
export const FONT_SIZES = [8, 9, 10, 11, 12, 13, 14, 15, 16, 18, 20, 22, 24, 28, 32, 36];

export type Align = "left" | "center" | "right" | "justify";

export interface BaseElement {
  id: string;
  x: number;
  y: number;
  width: number;
}

export interface TextElement extends BaseElement {
  type: "text";
  content: string;
  html: string;
  fontSize: number;
  fontFamily: string;
  color: string;
  bold: boolean;
  italic: boolean;
  underline: boolean;
  align: Align;
}

export interface ImageElement extends BaseElement {
  type: "image";
  content: string;
  src: string | null;
  height: number;
  /** QR-verification slot: `src` stays null, the image is generated from the letter's verify URL */
  isQr?: boolean;
}

export type CanvasElement = TextElement | ImageElement;
export type ElementPatch = Partial<TextElement> | Partial<ImageElement>;

export interface LockedBlock {
  label: string;
  x: number;
  y: number;
  width: number;
}

interface SchoolConfig {
  name: string;
  headerImage: string | null;
  footerImage: string | null;
}

export const SCHOOL = {
  name: "School",
  address: "",
  phone: "",
  website: "",
  email: "",
  headerImage: "",
  footerImage: "",
};

export function makeEl(o: Partial<TextElement> = {}): TextElement {
  const base: TextElement = {
    id: `el${Date.now()}`,
    type: "text",
    content: "Type here",
    html: "Type here",
    x: 80,
    y: 230,
    width: 420,
    fontSize: 13,
    fontFamily: "'DM Sans',sans-serif",
    color: "#000000",
    bold: false,
    italic: false,
    underline: false,
    align: "left",
  };
  const merged = { ...base, ...o };
  if (!merged.html) merged.html = merged.content;
  return merged;
}

export function makeImgEl(o: Partial<ImageElement> = {}): ImageElement {
  return {
    id: `el${Date.now()}`,
    type: "image",
    content: "Signature",
    src: null,
    x: 80,
    y: 700,
    width: 180,
    height: 70,
    ...o,
  };
}

/** QR-verification slot that every new letter starts with (bottom-left, draggable / deletable). */
export function makeQrEl(o: Partial<ImageElement> = {}): ImageElement {
  return makeImgEl({
    id: `qr${Date.now()}`,
    isQr: true,
    content: "QR verification",
    x: 50,
    y: PAGE_H - 270,
    width: 100,
    height: 100,
    ...o,
  });
}

export const isQrElement = (e: CanvasElement) => e.type === "image" && !!(e as ImageElement).isQr;

// ── QR code (vector, generated synchronously so buildPrint can stay sync) ──
export function qrSvgDataUri(text: string): string {
  const qr = createQr(text, { errorCorrectionLevel: "M" });
  const n = qr.modules.size;
  const data = qr.modules.data;
  const quiet = 2;
  const dim = n + quiet * 2;
  let path = "";
  for (let y = 0; y < n; y++) {
    for (let x = 0; x < n; x++) {
      if (data[y * n + x]) path += `M${x + quiet} ${y + quiet}h1v1h-1z`;
    }
  }
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${dim} ${dim}" shape-rendering="crispEdges">` +
    `<rect width="${dim}" height="${dim}" fill="#fff"/><path d="${path}" fill="#000"/></svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

// ── Undo / redo for the canvas elements ────────────────────────────────────
const HISTORY_LIMIT = 100;
const COALESCE_MS = 600;

type ElementsUpdater = CanvasElement[] | ((prev: CanvasElement[]) => CanvasElement[]);

export function useElementsHistory(initial: CanvasElement[] = []) {
  const [, force] = useState(0);
  const h = useRef({
    past: [] as CanvasElement[][],
    present: initial,
    future: [] as CanvasElement[][],
    lastKey: null as string | null,
    lastAt: 0,
  });

  /** Normal change. Changes with the same `coalesceKey` within COALESCE_MS merge into one undo step (typing, nudging a number). */
  const setEls = useCallback((next: ElementsUpdater, coalesceKey?: string) => {
    const s = h.current;
    const value = typeof next === "function" ? next(s.present) : next;
    if (value === s.present) return;
    const now = Date.now();
    const merge = coalesceKey !== undefined && coalesceKey === s.lastKey && now - s.lastAt < COALESCE_MS;
    if (!merge) {
      s.past.push(s.present);
      if (s.past.length > HISTORY_LIMIT) s.past.shift();
    }
    s.present = value;
    s.future = [];
    s.lastKey = coalesceKey ?? null;
    s.lastAt = now;
    force((n) => n + 1);
  }, []);

  /** Replace elements AND clear history (use for loading a saved letter / new blank letter). */
  const resetEls = useCallback((value: CanvasElement[]) => {
    const s = h.current;
    s.past = [];
    s.future = [];
    s.present = value;
    s.lastKey = null;
    force((n) => n + 1);
  }, []);

  const undo = useCallback(() => {
    const s = h.current;
    if (!s.past.length) return;
    s.future.push(s.present);
    s.present = s.past.pop()!;
    s.lastKey = null;
    force((n) => n + 1);
  }, []);

  const redo = useCallback(() => {
    const s = h.current;
    if (!s.future.length) return;
    s.past.push(s.present);
    s.present = s.future.pop()!;
    s.lastKey = null;
    force((n) => n + 1);
  }, []);

  return {
    elements: h.current.present,
    setEls,
    resetEls,
    undo,
    redo,
    canUndo: h.current.past.length > 0,
    canRedo: h.current.future.length > 0,
  };
}

export function buildPrint(
  elements: CanvasElement[],
  lockedBlocks: LockedBlock[],
  headerSrc: string | null,
  footerSrc: string | null,
  letterheadId: string = "",
  hideHeader = false,
  hideFooter = false,
  verifyUrl: string = "",
): string {
  const hTag = hideHeader
    ? ""
    : headerSrc
      ? `<img src="${headerSrc}" style="width:100%;display:block;-webkit-print-color-adjust:exact;print-color-adjust:exact;position:relative;z-index:1;" alt="header"/>`
      : `<div style="width:100%;height:160px;background:linear-gradient(135deg,#1e3a8a,#3b82f6);position:relative;z-index:1;"></div>`;

  const fTag = hideFooter
    ? ""
    : footerSrc
      ? `<img src="${footerSrc}" style="position:absolute;bottom:0;left:0;width:100%;display:block;-webkit-print-color-adjust:exact;print-color-adjust:exact;z-index:1;" alt="footer"/>`
      : `<div style="position:absolute;bottom:0;left:0;width:100%;height:110px;background:linear-gradient(135deg,#1e3a8a,#3b82f6);z-index:1;"></div>`;

  const elHTML = elements
    .map((el) => {
      if (el.type === "image") {
        const imgSrc = (el as ImageElement).isQr
          ? (verifyUrl ? qrSvgDataUri(verifyUrl) : null)
          : el.src;
        if (!imgSrc) return "";
        return `<div style="position:absolute;left:${el.x}px;top:${el.y}px;width:${el.width}px;height:${(el as ImageElement).height}px;z-index:2;"><img src="${imgSrc}" style="width:100%;height:100%;object-fit:contain;-webkit-print-color-adjust:exact;print-color-adjust:exact;" alt="signature"/></div>`;
      }
      return `<div style="position:absolute;left:${el.x}px;top:${el.y}px;width:${el.width}px;z-index:2;font-size:${el.fontSize}px;font-family:${el.fontFamily};color:${el.color};text-align:${el.align};line-height:1.6;white-space:pre-wrap;word-break:break-word;padding:4px 6px;">${el.html != null ? el.html : (el.content ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\n/g, "<br/>")}</div>`;
    })
    .join("");

  const isSerialBlock = (b: LockedBlock) =>
    b.label.toLowerCase().includes("s. no") ||
    b.label.toLowerCase().includes("serial") ||
    b.label.toLowerCase().includes("no.");

  let lockedHTML = lockedBlocks
    .filter((b) => !(letterheadId && isSerialBlock(b)))
    .map((b) =>
      `<div style="position:absolute;left:${b.x}px;top:${b.y}px;width:${b.width}px;z-index:2;font-size:13px;font-family:'DM Sans',sans-serif;color:#000000;line-height:1.6;padding:4px 6px;font-weight:600;">${b.label}</div>`
    )
    .join("");

  if (letterheadId) {
    const serialBlock = lockedBlocks.find((b) => isSerialBlock(b));
    if (serialBlock) {
      lockedHTML += `
        <div style="position:absolute;left:${serialBlock.x}px;top:${serialBlock.y}px;width:${serialBlock.width}px;z-index:2;font-size:13px;font-family:'DM Sans',sans-serif;color:#000000;line-height:1.6;padding:4px 6px;font-weight:600;">
          S. No. :-&nbsp;&nbsp;${letterheadId}
        </div>`;
    } else {
      lockedHTML += `
        <div style="position:absolute;left:260px;top:287px;z-index:2;font-size:13px;font-family:'DM Sans',sans-serif;color:#000000;line-height:1.6;padding:4px 6px;font-weight:600;">
          ${letterheadId}
        </div>`;
    }
  }

  return `<!DOCTYPE html><html><head><meta charset="UTF-8"/><link href="https://fonts.googleapis.com/css2?family=DM+Sans:ital,wght@0,400;0,700;1,400&family=Playfair+Display:wght@700&family=Lato:wght@400;700&family=Merriweather:wght@400;700&display=swap" rel="stylesheet"/><style>* { margin: 0; padding: 0; box-sizing: border-box; } @page { size: A4; margin: 0; } body { width: 794px; min-height: 1123px; background: white; } .page { position: relative; width: 794px; min-height: 1123px; background: white; overflow: hidden; }</style></head><body><div class="page">${hTag}<div style="position: absolute; top: 0; left: 0; right: 0; bottom: 0; z-index: 2;">${elHTML}${lockedHTML}</div>${fTag}</div></body></html>`;
}

export default function SchoolLetterHeader({ id, onNavigate }: CrudProps) {
  console.log('🔵 SchoolLetterHeader received ID:', id);
  console.log('🔵 Type of ID:', typeof id);
  console.log('🔵 ID length:', id?.length);

  const { optionFiles, loading: filesLoading } = useOptionFiles([
    "letterhead_header_image",
    "letterhead_footer_image",
  ]);

  const { options: userOptions, loading: userOptionsLoading } = useOptions([
    "school_name",
    "letterhead_serial",
    "letterhead_date",
  ]);

  const { elements, setEls, resetEls, undo, redo, canUndo, canRedo } = useElementsHistory();
  const [selId, setSelId] = useState<string | null>(null);
  const [toast, setToast] = useState("");
  const [printing, setPrinting] = useState(false);
  const [letterheadTitle, setLetterheadTitle] = useState("New Letterhead");
  const [zoom, setZoom] = useState(0.75);

  const [schoolConfig, setSchoolConfig] = useState<SchoolConfig>({ name: "School", headerImage: null, footerImage: null });
  const [lockedBlocks, setLockedBlocks] = useState<LockedBlock[]>([]);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [currentLetterheadId, setCurrentLetterheadId] = useState<string>("");
  const [verifyUrl, setVerifyUrl] = useState<string>("");
  const [isDirty, setIsDirty] = useState(false);

  const [hideHeader, setHideHeader] = useState(false);
  const [hideFooter, setHideFooter] = useState(false);

  const finalizedRef = useRef(false);

  const [savedSnapshot, setSavedSnapshot] = useState<string>("");
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const isDirtyRef = useRef(false);
  const pendingNavRef = useRef<string | number | null>(null);
  const elementsRef = useRef<CanvasElement[]>(elements);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => { elementsRef.current = elements; }, [elements]);

  useEffect(() => {
    const current = JSON.stringify({ title: letterheadTitle, elements });
    const dirty = current !== savedSnapshot;
    isDirtyRef.current = dirty;
    setIsDirty(dirty);
  }, [elements, letterheadTitle, savedSnapshot]);

  const handleApplyTemplate = (newElements: CanvasElement[]) => {
    // Applying a template must not remove the letter's own QR (unless the template brings one).
    const keepQr = newElements.some(isQrElement) ? [] : elements.filter(isQrElement);
    setEls([...newElements, ...keepQr]);
    setSelId(null);
    setIsDirty(true);
  };

  // ── Parse locked blocks from useOptions ───────────────────────────────────
  useEffect(() => {
    if (userOptionsLoading) return;

    const schoolName = userOptions.school_name?.data as string || "School";
    setSchoolConfig((prev) => ({ ...prev, name: schoolName }));

    const blocks: LockedBlock[] = [];

    const serialData = userOptions.letterhead_serial?.data;
    if (serialData) {
      let parsed: any = serialData;
      if (typeof serialData === 'string') {
        try { parsed = JSON.parse(serialData); } catch {}
      }
      if (parsed && typeof parsed.x === 'number' && typeof parsed.y === 'number') {
        blocks.push({ 
          label: "S. No. :-", 
          x: parsed.x, 
          y: parsed.y, 
          width: parsed.width || 440 
        });
      }
    }

    const dateData = userOptions.letterhead_date?.data;
    if (dateData) {
      let parsed: any = dateData;
      if (typeof dateData === 'string') {
        try { parsed = JSON.parse(dateData); } catch {}
      }
      if (parsed && typeof parsed.x === 'number' && typeof parsed.y === 'number') {
        blocks.push({ 
          label: "Date :-", 
          x: parsed.x, 
          y: parsed.y, 
          width: parsed.width || 200 
        });
      }
    }

    setLockedBlocks(blocks);
    console.log('🔵 Locked blocks:', blocks);
  }, [userOptionsLoading, userOptions]);

  // ── Set header/footer images from useOptionFiles ──────────────────────────
  useEffect(() => {
    if (filesLoading) return;

    const headerImage = optionFiles.letterhead_header_image?.files?.[0]?.url || null;
    const footerImage = optionFiles.letterhead_footer_image?.files?.[0]?.url || null;

    setSchoolConfig((prev) => ({ ...prev, headerImage, footerImage }));
  }, [filesLoading, optionFiles]);

  // ── Load letterhead or reset ──────────────────────────────────────────────
  useEffect(() => {
    if (userOptionsLoading || filesLoading || finalizedRef.current) return;
    finalizedRef.current = true;

    if (id) {
      console.log('🔵 Loading letterhead with ID:', id);
      loadLetterheadForEdit(String(id), lockedBlocks, {
        name: schoolConfig.name,
        headerImage: schoolConfig.headerImage,
        footerImage: schoolConfig.footerImage,
      }).finally(() => setOptionsLoading(false));
    } else {
      const startQr = makeQrEl();
      resetEls([startQr]);
      setSelId(null);
      setLetterheadTitle("New Letterhead");
      setVerifyUrl("");
      setSavedSnapshot(JSON.stringify({ title: "New Letterhead", elements: [startQr] }));
      setCurrentLetterheadId("");
      setIsDirty(false);
      setOptionsLoading(false);
    }
  }, [userOptionsLoading, filesLoading, lockedBlocks, schoolConfig.name, id]);

  const loadLetterheadForEdit = async (
    letterheadId: string,
    fallbackBlocks: LockedBlock[],
    fallbackConfig: SchoolConfig,
  ) => {
    try {
      console.log('🔵 Fetching letterhead with ID:', letterheadId);
      const response = await apiClient.get(`/api/letterheads?letterhead_id=${letterheadId}`);
      console.log('🔵 Response:', response.data);
      
      if (response.data.status === "success" && response.data.letterhead) {
        const lh = response.data.letterhead;
        const title = lh.title || "Edit Letterhead";

        const processedElements = (lh.elements || []).map((el: any) => {
          if (el.type === "text" && !el.html) return { ...el, html: el.content || "" };
          return el;
        });

        setLetterheadTitle(title);
        resetEls(processedElements);
        setSavedSnapshot(JSON.stringify({ title, elements: processedElements }));
        setCurrentLetterheadId(lh.letterhead_id || "");
        setVerifyUrl(lh.verify_url || "");

        if (lh.locked_blocks && lh.locked_blocks.length > 0) {
          const createdDate = lh.created_at
            ? new Date(lh.created_at.replace(" ", "T"))
                .toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" })
            : "";

          setLockedBlocks(lh.locked_blocks.map((b: LockedBlock) => {
            const isSerial =
              b.label.toLowerCase().includes("s. no") ||
              b.label.toLowerCase().includes("serial") ||
              b.label.toLowerCase().includes("no.");
            const isDate = b.label.toLowerCase().includes("date");

            if (isSerial) return { ...b, width: 440 };
            if (isDate)   return { ...b, label: `Date :- ${createdDate}` };
            return b;
          }));
        } else {
          setLockedBlocks(fallbackBlocks);
        }

        setSchoolConfig({
          name: fallbackConfig.name,
          headerImage: lh.header_image ?? fallbackConfig.headerImage,
          footerImage: lh.footer_image ?? fallbackConfig.footerImage,
        });
      }
    } catch (error) {
      console.error('❌ Load letterhead error:', error);
    }
  };

  const updateLockedBlock = (index: number, x: number, y: number, width?: number) => {
    setLockedBlocks(prev =>
      prev.map((b, i) => i === index ? { ...b, x, y, ...(width !== undefined ? { width } : {}) } : b)
    );
  };

  const safeNavigate = (to: string | number, opts?: NavigateOptions) => {
    if (isDirtyRef.current) {
      pendingNavRef.current = to;
      setShowUnsavedModal(true);
    } else {
      onNavigate(to as any, opts);
    }
  };

  const performSave = async (): Promise<false | true | { success: boolean; resolvedBlocks: LockedBlock[]; newLetterheadId: string; newVerifyUrl: string }> => {
    if (!letterheadTitle.trim()) { showToast("Please enter a title for this letterhead"); return false; }
    if (elements.length === 0)   { showToast("Add at least one element before saving");   return false; }

    setPrinting(true);
    try {
      const printHTML = buildPrint(elements, lockedBlocks, schoolConfig.headerImage, schoolConfig.footerImage, currentLetterheadId, false, false, verifyUrl);
      await generatePdfFromHtml(printHTML);

      const payload = {
        title: letterheadTitle.trim(),
        description: "",
        elements: elements,
        locked_blocks: lockedBlocks,
      };

      console.log('📤 Saving letterhead payload:', payload);

      const response = id
        ? await apiClient.put(`/api/letterheads/${id}`, payload)
        : await apiClient.post("/api/letterheads", payload);

      console.log('📥 Letterhead save response:', response.data);

      if (response.data?.status === "success") {
        showToast(id ? "Letterhead updated successfully" : "Letterhead saved successfully");
        setSavedSnapshot(JSON.stringify({ title: letterheadTitle.trim(), elements }));

        if (!id) {
          const newLetterheadId = response.data?.letterhead_id ?? "";
          const todayFormatted = new Date().toLocaleDateString("en-GB", {
            day: "2-digit", month: "2-digit", year: "numeric",
          });

          setCurrentLetterheadId(newLetterheadId);

          const resolvedBlocks = lockedBlocks.map(b => {
            const isSerial =
              b.label.toLowerCase().includes("s. no") ||
              b.label.toLowerCase().includes("serial") ||
              b.label.toLowerCase().includes("no.");
            const isDate = b.label.toLowerCase().includes("date");

            if (isSerial) return { ...b, width: 440 };
            if (isDate)   return { ...b, label: `Date :- ${todayFormatted}` };
            return b;
          });

          setLockedBlocks(resolvedBlocks);

          const newVerifyUrl: string = response.data?.verify_url ?? "";
          setVerifyUrl(newVerifyUrl);

          const newId = response.data?.letterhead?.id ?? response.data?.id;
          if (newId) onNavigate(`/letterheads/edit/${newId}`, { replace: true });

          return { success: true, resolvedBlocks, newLetterheadId, newVerifyUrl };
        } else {
          if (response.data.locked_blocks?.length > 0) {
            setLockedBlocks(response.data.locked_blocks);
          }
          return true;
        }
      }

      showToast("Save failed");
      return false;
    } catch (error: any) {
      console.error('❌ Save error:', error);
      showToast(error.response?.data?.message || "Failed to save. Please try again.");
      return false;
    } finally {
      setPrinting(false);
    }
  };

  const handleSaveAndPrint = async () => {
    const result = await performSave();
    if (!result) return;

    const printBlocks = typeof result === 'object' && result.resolvedBlocks
      ? result.resolvedBlocks
      : lockedBlocks;

    const printLetterheadId = typeof result === 'object' && result.newLetterheadId !== undefined
      ? result.newLetterheadId
      : currentLetterheadId;

    const printVerifyUrl = typeof result === 'object' && result.newVerifyUrl !== undefined
      ? result.newVerifyUrl
      : verifyUrl;

    const printHTML = buildPrint(
      elements,
      printBlocks,
      schoolConfig.headerImage,
      schoolConfig.footerImage,
      printLetterheadId,
      hideHeader,
      hideFooter,
      printVerifyUrl,
    );

    const w = window.open("", "_blank");
    if (w) {
      w.document.write(printHTML);
      w.document.close();
      setTimeout(() => { w.focus(); w.print(); }, 800);
    } else {
      showToast("Pop-up blocked — please allow pop-ups to print.");
    }
  };

  const handlePrintOnly = () => {
    const printHTML = buildPrint(
      elements,
      lockedBlocks,
      schoolConfig.headerImage,
      schoolConfig.footerImage,
      currentLetterheadId,
      hideHeader,
      hideFooter,
      verifyUrl,
    );
    const w = window.open("", "_blank");
    if (w) {
      w.document.write(printHTML);
      w.document.close();
      setTimeout(() => { w.focus(); w.print(); }, 800);
    } else {
      showToast("Pop-up blocked — please allow pop-ups to print.");
    }
  };

  useEffect(() => {
    window.history.pushState({ _letterheadSentinel: true }, "");
    const onPopState = () => {
      if (!isDirtyRef.current) return;
      window.history.pushState({ _letterheadSentinel: true }, "");
      pendingNavRef.current = -1;
      setShowUnsavedModal(true);
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (!isDirtyRef.current) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, []);

  const showToast = (m: string) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast(m);
    toastTimerRef.current = setTimeout(() => setToast(""), 2400);
  };

  const addEl = (o: Partial<TextElement> = {}) => {
    const el = makeEl(o);
    setEls((p) => [...p, el]);
    setSelId(el.id);
  };

  const addImgEl = (o: Partial<ImageElement> = {}) => {
    const el = makeImgEl(o);
    setEls((p) => [...p, el]);
    setSelId(el.id);
  };

  const updateEl = (elId: string, patch: ElementPatch) => {
    const keys = Object.keys(patch);
    setEls(
      (p) => {
        const cur = p.find((e) => e.id === elId);
        if (!cur || keys.every((k) => (cur as any)[k] === (patch as any)[k])) return p;
        return p.map((e) => (e.id === elId ? ({ ...e, ...patch } as CanvasElement) : e));
      },
      `${elId}:${keys.sort().join(",")}`,
    );
  };

  const deleteEl = (elId: string) => {
    setEls((p) => p.filter((e) => e.id !== elId));
    if (selId === elId) setSelId(null);
  };

  const duplicateEl = (elId: string) => {
    const s = elementsRef.current.find((e) => e.id === elId);
    if (!s) return;
    if (s.type === "image") {
      const el = makeImgEl({ ...s, id: undefined as any, x: s.x + 14, y: s.y + 14 });
      setEls((p) => [...p, el]);
      setSelId(el.id);
    } else {
      addEl({ ...(s as TextElement), id: undefined as any, x: s.x + 14, y: s.y + 14 });
    }
  };

  async function generatePdfFromHtml(html: string): Promise<Blob> {
    return new Promise((resolve, reject) => {
      try {
        const element = document.createElement("div");
        element.innerHTML = html;
        document.body.appendChild(element);
        html2canvas(element, { scale: 2, logging: false, useCORS: true })
          .then((canvas) => {
            const pdf = new jsPDF("p", "mm", "a4");
            pdf.addImage(canvas.toDataURL("image/jpeg", 0.98), "JPEG", 0, 0, 210, 297);
            const blob = pdf.output("blob");
            document.body.removeChild(element);
            resolve(blob);
          })
          .catch((err) => { document.body.removeChild(element); reject(err); });
      } catch (err) {
        reject(err);
      }
    });
  }

  const handleNewLetterhead = () => safeNavigate("/letterheads/new");

  const handleModalKeepEditing = () => { pendingNavRef.current = null; setShowUnsavedModal(false); };

  const handleModalDiscard = () => {
    setSavedSnapshot(JSON.stringify({ title: letterheadTitle, elements }));
    isDirtyRef.current = false;
    setIsDirty(false);
    const to = pendingNavRef.current;
    pendingNavRef.current = null;
    setShowUnsavedModal(false);
    if (to !== null) onNavigate(to as any);
  };

  const handleModalSave = async () => {
    const saved = await performSave();
    if (saved) {
      const to = pendingNavRef.current;
      pendingNavRef.current = null;
      setShowUnsavedModal(false);
      if (to !== null) onNavigate(to as any);
    }
  };

  if (optionsLoading || userOptionsLoading || filesLoading) {
    return <LetterEditorSkeletonLoader />;
  }

  return (
    <>
      <LetterEditor
        elements={elements}
        selId={selId}
        zoom={zoom}
        letterheadTitle={letterheadTitle}
        printing={printing}
        onTitleChange={setLetterheadTitle}
        onAddEl={addEl}
        onAddImgEl={addImgEl}
        onUpdateEl={updateEl}
        onDeleteEl={deleteEl}
        onDuplicateEl={duplicateEl}
        onSaveAndPrint={handleSaveAndPrint}
        onSetSelId={setSelId}
        SCHOOL={{
          ...SCHOOL,
          name: schoolConfig.name,
          headerImage: schoolConfig.headerImage ?? "",
          footerImage: schoolConfig.footerImage ?? "",
        }}
        PAGE_W={PAGE_W}
        PAGE_H={PAGE_H}
        SNAP={SNAP}
        FONT_SIZES={FONT_SIZES}
        isEditing={!!id}
        onNewLetterhead={handleNewLetterhead}
        lockedBlocks={lockedBlocks}
        onUpdateLockedBlock={updateLockedBlock}
        currentLetterheadId={currentLetterheadId}
        isDirty={isDirty}
        onPrintOnly={handlePrintOnly}
        onApplyTemplate={handleApplyTemplate}
        onZoomChange={setZoom}
        hideHeader={hideHeader}
        hideFooter={hideFooter}
        onHideHeaderChange={setHideHeader}
        onHideFooterChange={setHideFooter}
        verifyUrl={verifyUrl}
        onUndo={undo}
        onRedo={redo}
        canUndo={canUndo}
        canRedo={canRedo}
      />

      <UnsavedChangesModal
        open={showUnsavedModal}
        onClose={handleModalKeepEditing}
        onDiscard={handleModalDiscard}
        onSave={handleModalSave}
        saving={printing}
      />

      {toast && (
        <div style={{
          position: "fixed", bottom: 28, left: "50%", transform: "translateX(-50%)",
          background: "#1e3a8a", color: "white", padding: "10px 22px",
          borderRadius: 10, fontSize: 13, fontWeight: 600, zIndex: 99999,
          boxShadow: "0 4px 18px rgba(30,58,138,0.3)",
          animation: "fadeInUp 0.3s ease",
        }}>
          {toast}
        </div>
      )}
    </>
  );
}